/**
 * Spotlight definitions for the V1 search-results experience.
 *
 * Three locked defaults per the V1 brief and Confluence "New search results
 * experience — V1" (page 399769612, Jira TD-2200/TD-2201). Each spotlight is a
 * one-click "smart cut" applied as an AND-intersection against the current
 * filtered set.
 *
 * The `match(company, ctx, allResults)` predicate returns true if the company
 * is part of this spotlight's cut. `ctx` is a precomputed object holding any
 * quartile / threshold values the predicate needs — built once per filter pass
 * by buildSpotlightContext() in exploreFilterEngine.js.
 *
 * Icons reference the shared `ICON_BUTTON_ICONS` enum from IconButton.jsx so
 * the SVG sprite stays the single source of truth for iconography.
 */

import { ICON_BUTTON_ICONS } from '../../../../components/buttons/IconButton';
import { arrToNames, getQuartile } from './exploreFilterEngine';
import { displayScale } from './columnFilterTypes';
import { PROFILER_SEARCH_RESULTS_COLUMNS } from './constants';
import { UI_OPTION_KEYS } from '../../../../utils/constants/uiOptionKeys';
import { UI_OPTIONS } from '../../../../utils/constants/uiOptions';

// High-growth threshold expressed in **display units** (i.e. "10%"). The
// match predicate scales it to raw storage units via `displayScale('cagr3Y')`
// so it works consistently against the BE's fractional `_cagr3Y` values
// (e.g. 0.056 = 5.6%).
const HIGH_GROWTH_THRESHOLD_DISPLAY = 10;

/**
 * @typedef {Object} SpotlightDefinition
 * @property {string}   id          Stable identifier; persisted in localStorage.
 * @property {string}   label       Headline label rendered on the chip.
 * @property {string}   subtitle    Short explanatory line under the label.
 * @property {string}   icon        Value from ICON_BUTTON_ICONS — SVG path string.
 * @property {Function} match       (company, ctx, allResults) => boolean.
 */

export const SPOTLIGHT_DEFINITIONS = Object.freeze([
  Object.freeze({
    id: 'succession',
    label: 'Succession',
    subtitle: 'Privately owned, majority shareholder 50+',
    icon: ICON_BUTTON_ICONS.BULLSEYE,
    /**
     * Companies whose `situation` tag-array contains a "Succession" tag.
     *
     * The situation values come from the backend's `profilerSituations`
     * option set, so we can't pin the exact string in the codebase. The
     * match is case-insensitive and accepts the German variant "Nachfolge"
     * (substring), so any tag whose name contains either keyword qualifies.
     *
     * Tolerates both data shapes: Array<{name}> (real codebase) and
     * Array<string> (prototype / tests).
     */
    match: (company) => {
      const names = arrToNames(company?.situation);
      return names.some((n) => {
        const lower = n.toLowerCase();
        return lower.includes('succession') || lower.includes('nachfolge');
      });
    },
    /**
     * V1.7 — Spotlight ↔ column sync. Clicking Succession populates the
     * Situation column filter with `succession` checked, so the analyst sees
     * a coherent state (chip active + column ⌄ active + popover already
     * checked). The `match()` predicate above accepts substring "nachfolge"
     * too, but the BE only emits two enum values for situation right now
     * (`succession`, `loss-making`) — so an exact-value filter is sufficient
     * and matches what the popover would store from a manual click.
     */
    relatedColumn: {
      columnKey: 'situation',
      columnId: PROFILER_SEARCH_RESULTS_COLUMNS.SITUATION,
      computeFilterValue: () => ({ values: new Set(['succession']) })
    }
  }),

  Object.freeze({
    id: 'top-ebit',
    label: 'Top-quartile EBIT',
    subtitle: 'Margin in top 25% of results',
    icon: ICON_BUTTON_ICONS.GRAPH_UP,
    /**
     * `ebitPercentage` ≥ 75th-percentile threshold across all loaded results.
     *
     * The threshold is STATIC against `state.results` (the loaded set) rather
     * than dynamic against the currently-filtered subset. This matches the V1
     * prototype and keeps "top quartile" a stable, meaningful concept — it
     * doesn't shift as the user narrows the filtered view.
     *
     * The threshold is pre-computed once per filter pass by
     * buildSpotlightContext() and passed in via `ctx`.
     */
    match: (company, ctx) => {
      // Use the underscore-prefixed numeric copy — `company.ebitPercentage`
      // is a pre-formatted display string like "8.3%". `_ebitPercentage` is
      // the numeric counterpart used by sort + filter logic.
      const raw = company?._ebitPercentage ?? company?.ebitPercentage;
      const v = raw == null ? null : Number(raw);
      if (v === null || !Number.isFinite(v)) return false;
      const threshold = ctx?.quartiles?.ebitPercentage_p75;
      if (threshold == null) return false;
      return v >= threshold;
    },
    /**
     * V1.7 — Spotlight ↔ column sync. Top-quartile is computed dynamically
     * against the loaded result set: the 75th percentile of `_ebitPercentage`
     * across all non-excluded companies. The popover then shows this
     * threshold pre-filled as the Min (e.g. `12.3` when the raw p75 is
     * 0.123 with the V1.5 % display-scale of 0.01).
     */
    relatedColumn: {
      columnKey: 'ebitPercentage',
      columnId: PROFILER_SEARCH_RESULTS_COLUMNS.EBIT_MARGIN,
      computeFilterValue: (allResults) => {
        // Return null (not a phantom `{ range: { min: null } }`) when the set
        // has no parseable EBIT data — `setColumnFilter` then clears the
        // column instead of leaving the ⌄ lit on an always-true filter.
        const min = getQuartile(allResults, 'ebitPercentage', 0.75);
        return min == null ? null : { range: { min } };
      }
    }
  }),

  Object.freeze({
    id: 'high-growth',
    label: 'High growth',
    subtitle: 'CAGR 3Y ≥ 10%',
    icon: ICON_BUTTON_ICONS.ROCKET,
    /**
     * Companies with reported 3-year revenue CAGR of 10% or higher.
     * Excludes n/a. (V1.3 lowered the threshold from > 15% to ≥ 10%.)
     */
    match: (company) => {
      // `company.cagr3Y` is the display string ("18.8%"); `_cagr3Y` is the
      // numeric copy used by sort + filter logic. The BE returns it as a
      // fraction (e.g. 0.188 = 18.8%), so the threshold has to be scaled
      // from display units (10 → 10%) into the raw storage scale (0.1).
      const raw = company?._cagr3Y ?? company?.cagr3Y;
      const v = raw == null ? null : Number(raw);
      if (v == null || !Number.isFinite(v)) return false;
      const threshold = HIGH_GROWTH_THRESHOLD_DISPLAY * displayScale('cagr3Y');
      return v >= threshold;
    },
    /**
     * V1.7 — Spotlight ↔ column sync. Mirrors the predicate's threshold so
     * the popover shows `10` as Min (display units) and stores `0.10` raw
     * via `FilterNumericRange`'s scale conversion. Filter result is
     * identical to the Spotlight predicate's narrowing (intersection of
     * the two routes targets the same rows).
     */
    relatedColumn: {
      columnKey: 'cagr3Y',
      columnId: PROFILER_SEARCH_RESULTS_COLUMNS.CAGR3Y,
      computeFilterValue: () => ({
        range: { min: HIGH_GROWTH_THRESHOLD_DISPLAY * displayScale('cagr3Y') }
      })
    }
  }),

  Object.freeze({
    id: 'high-match',
    label: 'High matches',
    subtitle: 'AI rationale rated High',
    icon: ICON_BUTTON_ICONS.STAR_FILL,
    /**
     * Companies whose AI-scoring badge reads "High" (the `scoreLabel` field
     * rendered as the badge in CompanyResult.jsx, gated by `showScoring`).
     * Companies without `showScoring` are treated as not-a-high-match — the
     * badge isn't even visible for them, so they wouldn't have a "High"
     * rationale to filter on. Case-insensitive equality.
     */
    match: (company) => {
      if (!company?.showScoring) return false;
      const label = company.scoreLabel;
      return typeof label === 'string' && label.toLowerCase() === 'high';
    }
    // V1.7 — no `relatedColumn`. `scoreLabel` isn't surfaced as a filterable
    // column (the AI badge is row-decoration, not a header), so this stays
    // Spotlight-only. Clicking the chip narrows via the predicate; no column
    // ⌄ lights up.
  })
]);

/**
 * Lookup by ID. Returns undefined if not found.
 */
export function getSpotlightById(id) {
  return SPOTLIGHT_DEFINITIONS.find((s) => s.id === id);
}

/**
 * All spotlight IDs, in display order.
 */
export const SPOTLIGHT_IDS = Object.freeze(SPOTLIGHT_DEFINITIONS.map((s) => s.id));

/**
 * TD-2898: which Spotlights must not be offered in the current context.
 *
 * One shared rule for both surfaces that mount the Add-filter panel and the
 * Spotlights bar — `SearchResults/index.jsx` and
 * `ProfilerProjects/.../ProfilerSearchResultsTabView.jsx`. The two stores each
 * derived this inline, and the projects copy never picked up the EBIT branch
 * TD-2499 added to the search copy, so a Profiler Lite account was still handed
 * a live "Top-quartile EBIT" chip on saved projects that activated and then
 * reported `0 of N match`.
 *
 * The returned IDs are hidden from the chip row AND dropped from the filter
 * pass (see `filteredResults` in both stores), so a stale ID inherited from
 * localStorage cannot silently empty the list with no chip to explain it.
 *
 * `top-ebit` is withheld on either of two grounds, and both are wanted:
 *
 * - `isProfilerLiteOnly` — the tier rule TD-2499 applies to the EBIT filter
 *   dimension, the EBIT column and the heatmap colour-coding option. Keeping it
 *   means the chip stays hidden even if a future payload were to carry margins
 *   the plan does not include.
 * - `!hasEbitData` — the data rule TD-2890 applies to the sort menus, which
 *   answers correctly whatever tier rule the BE happens to use. It also covers a
 *   Full account whose result set carries no margins at all, where the chip was
 *   a dead control offering a permanent 0.
 *
 * Returns `null` rather than an empty Set when nothing is excluded — callers
 * treat the absence of a set as "no exclusions" and skip the filter pass.
 *
 * @param {Object}  args
 * @param {string}  [args.searchCategory]     The search's Profiler category.
 * @param {boolean} [args.isProfilerLiteOnly] Viewer is on a Lite-only plan.
 * @param {boolean} [args.hasEbitData]        Result set carries EBIT margins.
 * @returns {Set<string>|null}
 */
export function buildExcludedSpotlightIds({
  searchCategory,
  isProfilerLiteOnly = false,
  hasEbitData = false
} = {}) {
  const excluded = new Set();

  // "High matches" reads the AI-rationale badge, which Industry searches never
  // carry — hide it rather than showing a permanent 0. Bulk uploads carry no AI
  // judgement either: their rows show what the user uploaded instead (TD-2992).
  const categories = UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY];
  if (searchCategory === categories['Industries'] || searchCategory === categories['Bulk']) {
    excluded.add('high-match');
  }

  if (isProfilerLiteOnly || !hasEbitData) {
    excluded.add('top-ebit');
  }

  return excluded.size ? excluded : null;
}
